export interface ParsedContent {
  text: string;
  imagesCount: number;
  filesCount: number;
  integrationsCount: number;
  linksCount: number;
}

export const postContentParser = (contentStr: string): ParsedContent => {
  let text = '';
  let imagesCount = 0;
  let filesCount = 0;
  let integrationsCount = 0;
  let linksCount = 0;

  if (!contentStr) {
    return { text, imagesCount, filesCount, integrationsCount, linksCount };
  }

  try {
    const parsed = JSON.parse(contentStr);

    // 1. Slate format text extraction
    if (parsed && Array.isArray(parsed.value)) {
      const traverse = (node: any): string => {
        if (typeof node.text === 'string') {
          return node.text;
        }
        if (Array.isArray(node.children)) {
          return node.children.map(traverse).join('');
        }
        return '';
      };

      text = parsed.value
        .map((block: any) => traverse(block))
        .filter((val: string) => val.trim().length > 0)
        .join('\n');
    } else if (parsed && Array.isArray(parsed.blocks)) {
      text = parsed.blocks
        .map((block: any) => block.text || '')
        .filter((val: string) => val.trim().length > 0)
        .join('\n');
    }

    if (parsed && parsed.context?.entities && Array.isArray(parsed.value)) {
      const referencedEntityIds = new Set<string>(
        parsed.value
          .filter((block: any) => block.type === 'fileBlock' && block.entity)
          .map((block: any) => block.entity)
      );

      referencedEntityIds.forEach((entityId) => {
        const entity = parsed.context.entities[entityId];
        if (entity && entity.type === 'file') {
          const fileType = entity.data?.type || entity.file?.type || '';
          if (fileType.startsWith('image/')) {
            imagesCount++;
          } else {
            filesCount++;
          }
        }
      });

      // Count integrations and assignments by scanning all entities
      // referenced from value blocks. All LTI-based integrations (Talis,
      // Panopto, Echo360, H5P, BibliU, PlanetEStream, BigBlueButton,
      // genericLti) share entity type 'lti', while assignments use 'assignment'.
      const allReferencedEntityIds = new Set<string>(
        parsed.value
          .filter((block: any) => block.entity)
          .map((block: any) => block.entity)
      );

      allReferencedEntityIds.forEach((entityId) => {
        const entity = parsed.context.entities[entityId];
        if (entity && (entity.type === 'lti' || entity.type === 'assignment')) {
          integrationsCount++;
        }
        if (entity && entity.type === 'meta') {
          linksCount++;
        }
      });
    }
    // 2. Draft.js format entities extraction
    if (parsed && parsed.entityMap && typeof parsed.entityMap === 'object') {
      Object.values(parsed.entityMap).forEach((entity: any) => {
        const entityType = (entity.type || '').toUpperCase();
        if (entityType.includes('IMAGE')) {
          imagesCount++;
        } else if (
          entityType.includes('FILE') ||
          entityType.includes('DOCUMENT')
        ) {
          filesCount++;
        }
      });
    }
  } catch (e) {
    // If parsing fails, it's probably raw text content
    text = contentStr;
  }

  return { text, imagesCount, filesCount, integrationsCount, linksCount };
};
